import { Input, makeStyles } from '@fluentui/react-components';
import { Search, X } from 'lucide-react';
import { Button } from '@fluentui/react-components';

import { IdentityWorkbench } from '../../hooks/useIdentityWorkbench';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    flexWrap: 'wrap',
    rowGap: '6px',
  },
  input: {
    minWidth: '280px',
    flexGrow: 1,
    maxWidth: '420px',
  },
  note: {
    fontSize: '12px',
    color: '#52525b',
  },
});

export interface IdentityQueueSearchProps {
  wb: IdentityWorkbench;
}

/**
 * FD-508 — a search over the queue, served by FD-506's `search` parameter.
 *
 * It is SERVER-side, and that is the point. `get-identity-suggestions` narrows
 * the deduped user list before its page window, so the matches come back as a
 * full page rather than as whatever the browser happened to have loaded. A
 * client-side filter over loaded pages would have made the box untrustworthy on
 * exactly the campaigns it exists for — at 2,847 identities an operator would
 * have had to press "Load more" fifty-seven times before it told the truth.
 *
 * What it matches is the DEVICE-REPORTED facts and the SID, because those are
 * what the server holds without a Graph call. It does not search the
 * destination tenant — that is what the add panel and the Change picker do.
 */
export default function IdentityQueueSearch({ wb }: IdentityQueueSearchProps) {
  const styles = useStyles();
  const { searchInput, setSearchInput, searchActive, matchedUserCount, totalUniqueUsers } =
    wb;

  const tooShort = searchInput.trim().length === 1;

  return (
    <div className={styles.row}>
      <Input
        className={styles.input}
        value={searchInput}
        onChange={(_, data) => setSearchInput(data.value)}
        placeholder="Search by name, sign-in name, or SID"
        aria-label="Search identities"
        contentBefore={<Search size={16} strokeWidth={1.5} />}
        contentAfter={
          searchInput ? (
            <Button
              size="small"
              appearance="transparent"
              aria-label="Clear identity search"
              icon={<X size={14} strokeWidth={1.5} />}
              onClick={() => setSearchInput('')}
            />
          ) : undefined
        }
      />
      {tooShort && (
        <span className={styles.note}>Type at least 2 characters to search.</span>
      )}
      {searchActive && matchedUserCount !== undefined && (
        <span className={styles.note}>
          {matchedUserCount.toLocaleString()} of {totalUniqueUsers.toLocaleString()}{' '}
          identit{matchedUserCount === 1 ? 'y' : 'ies'} match
        </span>
      )}
    </div>
  );
}
